import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* ------------------------------------------------------------------ *
 * Inline SVG icons (no extra dependency)                              *
 * ------------------------------------------------------------------ */
const Icon = {
  Search: (p) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
    </svg>
  ),
  Gavel: (p) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M14 13l-7.5 7.5a2.1 2.1 0 11-3-3L11 10" />
      <path d="M16 16l6-6" /><path d="M8 8l6-6" /><path d="M9 7l8 8" /><path d="M21 21H10" />
    </svg>
  ),
  Megaphone: (p) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 11l18-8v18l-18-8z" /><path d="M11.6 16.8a3 3 0 11-5.8-1.6" />
    </svg>
  ),
  Healing: (p) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M11 2L2 11l11 11 9-9z" /><path d="M8.5 8.5l7 7" />
    </svg>
  ),
  Lightbulb: (p) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M12 2a7 7 0 00-4 12.74V17a2 2 0 002 2h4a2 2 0 002-2v-2.26A7 7 0 0012 2zm-2 19a2 2 0 004 0v-1h-4z" />
    </svg>
  ),
  Chevron: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  Pdf: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  ),
  Sheet: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M9 4v16" />
    </svg>
  ),
  Share: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  ),
  Sun: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  ),
  Moon: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  ),
  Bolt: (p) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M13 2L3 14h7l-1 8 10-12h-7z" />
    </svg>
  ),
};

/* ------------------------------------------------------------------ *
 * Helpers                                                             *
 * ------------------------------------------------------------------ */
// 'review' ranks alongside 'medium' (REVIEW_REQUIRED visually reuses the
// medium/amber treatment — see riskBucket()'s doc comment).
const _RISK_RANK = { low: 0, medium: 1, review: 1, high: 2 };

// Confidence floor below which a COSMETIC/OTC_DRUG verdict must never render
// green, mirrored from the backend's REVIEW_CONFIDENCE_FLOOR setting. Kept in
// sync manually — see .claude/TODO/validation-error-fix-7-13.md D2.
export const REVIEW_CONFIDENCE_FLOOR = 0.60;

// Three-band confidence display (Claim-valid-error-fix-8-19.md §8 T5.1).
//
// On the rule-veto path the backend's ensemble confidence is a hard floor —
// `max(0.90, rule_result.confidence)` — not a measurement. A single
// context-free keyword hit with zero corroborating precedent has shown up to
// the customer as "규정 매칭도 90%". Rather than wait on (or assume) a backend
// fix, the report never renders that number as a headline percentage again;
// it renders one of three honest bands instead. The raw value is still put
// in the DOM (a `title` attribute) so support can see it on request.
//
// Boundaries, chosen from the rule engine's own observed confidences across
// the incident's variant sweep (single/double-hit vetoes: 0.38, 0.55, 0.60,
// 0.64, 0.65, 0.69, 0.73, 0.77):
//   - MODERATE_MIN reuses REVIEW_CONFIDENCE_FLOOR (0.60) so "low" here means
//     the same thing riskBucket() already means by "not enough signal to
//     trust" — one constant, one definition, instead of two thresholds that
//     could silently drift apart.
//   - HIGH_MIN (0.80) sits above every observed single/double-hit rule
//     confidence (max 0.77), so a lone, context-free keyword match can never
//     earn "High" — only a genuinely well-corroborated verdict (multiple
//     hits and/or real precedent) can.
// Caveat: until the backend veto floor itself is fixed, a still-floored 0.90
// will band as "High" — this UI change makes the number honest, not the
// floor. See the task report for the backend follow-up.
export const CONFIDENCE_BAND_MODERATE_MIN = REVIEW_CONFIDENCE_FLOOR; // 0.60
export const CONFIDENCE_BAND_HIGH_MIN = 0.80;

export function confidenceBand(confidence) {
  if (typeof confidence !== 'number' || Number.isNaN(confidence)) return null;
  if (confidence >= CONFIDENCE_BAND_HIGH_MIN) return 'high';
  if (confidence >= CONFIDENCE_BAND_MODERATE_MIN) return 'moderate';
  return 'low';
}

// Severity implied purely by the canonical risk_level enum (English).
// Returns null for empty / unrecognized input (e.g. a localized string),
// which the caller handles fail-safe.
function _bucketFromLevel(level) {
  const k = String(level || '').toUpperCase().trim();
  if (k === 'CRITICAL' || k === 'HIGH') return 'high';
  if (k === 'MEDIUM' || k === 'CAUTION') return 'medium';
  if (k === 'LOW' || k === 'SAFE') return 'low';
  return null;
}

// Minimum severity a classification must always surface at. A DRUG (or OTC drug)
// claim must never render as "safe", even if risk_level disagrees or is missing.
function _floorFromClassification(classification) {
  const k = String(classification || '').toUpperCase().trim();
  if (k === 'DRUG') return 'high';
  if (k === 'OTC_DRUG' || k === 'OTC') return 'medium';
  return 'low';
}

/**
 * Canonical severity bucket for the report UI:
 * 'low' | 'medium' | 'high' | 'review' | 'disclaimer'.
 *
 * IMPORTANT: pass the *canonical English enums* (risk_level_en / classification_en),
 * never localized display strings — localized values ("심각", "의약품") are display-only
 * and must not drive logic (that was the green-vs-severe disparity bug).
 *
 * 'review' and 'disclaimer' are special, classification-driven terminal states
 * (checked before the level/floor math below):
 *  - classification === 'ENGLISH_ONLY' or 'NOT_APPLICABLE' → 'disclaimer' — a
 *    neutral disclaimer outcome, NOT a verdict. ENGLISH_ONLY means non-English
 *    input was not analyzed/classified; NOT_APPLICABLE means no cosmetics/drug/
 *    OTC claim was detected (off-domain input), also never analyzed/classified.
 *    Neither is billable or review-queued; the caller must render the disclaimer
 *    banner instead of a 🔴/🟡/🟢 traffic light or a review CTA.
 *  - classification === 'REVIEW_REQUIRED' → 'review' — always 🟡 "Needs review".
 *    Ranks with 'medium' ({@link _RISK_RANK}) so it visually reuses the existing
 *    medium/amber styling; callers that need the literal CSS/i18n suffix should
 *    collapse it to 'medium' (see _cssBucket below).
 *
 * Otherwise:
 *  - result = max(severity from risk_level, floor from classification)
 *  - unknown / unrecognized risk → fail-safe (never silently "low")
 *
 * `confidence` is accepted for call-signature compatibility but NO LONGER
 * AFFECTS THE BUCKET. It used to impose a low-confidence floor mirroring the
 * backend's D1 gate; the backend retired that gate and this mirror outlived it,
 * turning compliant appearance-qualified claims amber. See T1.0 in
 * .claude/TODO/claim-valid-error-flip-9-16.md and the note in the body.
 *
 * Guarantees a DRUG/OTC_DRUG claim can never render green/safe, and that both
 * report formats compute the same status.
 */
export function riskBucket(level, classification, confidence) {
  const cls = String(classification || '').toUpperCase().trim();

  // Non-English input, or input with no regulated claim at all, was never
  // classified — a disclaimer, not a verdict (NON-COSMETICS-REROUTE-TO-HUMAN-EXPERT-8-1.md).
  if (cls === 'ENGLISH_ONLY' || cls === 'NOT_APPLICABLE') return 'disclaimer';

  // The fail-safe sink: always 🟡, never green, regardless of risk_level.
  if (cls === 'REVIEW_REQUIRED') return 'review';

  const fromLevel = _bucketFromLevel(level);
  const floor = _floorFromClassification(classification);
  const k = String(level || '').toUpperCase().trim();
  let base;
  if (fromLevel) {
    base = fromLevel;                          // recognized enum
  } else if (k === '') {
    base = floor;                              // empty → trust classification
  } else {
    base = floor === 'low' ? 'medium' : floor; // unrecognized/bad data → never 'low'
  }
  const bucket = _RISK_RANK[floor] > _RISK_RANK[base] ? floor : base;

  // T1.0 (claim-valid-error-flip-9-16.md) — THE CONFIDENCE FLOOR USED TO LIVE
  // HERE, AND ITS REMOVAL IS THE FIX, NOT AN OVERSIGHT.
  //
  // This mirrored the backend's D1 fail-safe floor: a COSMETIC/OTC_DRUG verdict
  // below REVIEW_CONFIDENCE_FLOOR was forced to 'medium' so it could never
  // render green. The backend then RETIRED that floor — apply_failsafe_gate is
  // behind `human_review_enabled`, which is False in production — because the
  // floor was turning every zero-rule-hit cosmetic claim into "판정 미완료"
  // (review_gate.py:225-233 says so in as many words). The mirror here was never
  // removed, and ReportSections' own comment admitted it was "kept in sync
  // manually". The sync was missed.
  //
  // The cost: "Reduces the appearance of wrinkles" — compliant, appearance-only
  // phrasing the product itself recommends — scored 0.40 with zero rule hits and
  // rendered 주의, while "Improves wrinkles" scored 0.72 and rendered 통과. The
  // customer reported it as 반대로, and they were right.
  //
  // Deleting it is safe in BOTH flag states, which is the point:
  //   flag ON  -> the backend emits classification 'REVIEW_REQUIRED', and the
  //               early return above catches it before this code is reached.
  //               The branch was unreachable.
  //   flag OFF -> the branch fired on a verdict the backend had deliberately
  //               decided to emit as-is. The branch was wrong.
  //
  // Do NOT reintroduce a confidence floor here. If a "no evidence either way"
  // state is wanted, it belongs in the backend as an explicit display bucket —
  // not in the frontend guessing at the backend's confidence.
  //
  // REVIEW_CONFIDENCE_FLOOR itself is deliberately kept: it still defines
  // CONFIDENCE_BAND_MODERATE_MIN for the three-band confidence display.
  return bucket;
}

// Collapses the semantic bucket to the literal CSS/i18n suffix used by the
// shared traffic-light styling: 'review' visually reuses 'medium'; 'disclaimer'
// has its own non-traffic-light treatment and takes no severity suffix.
function _cssBucket(bucket) {
  if (bucket === 'review') return 'medium';
  if (bucket === 'disclaimer') return '';
  return bucket;
}

export function classificationLabel(c) {
  const k = String(c || '').toUpperCase().trim();
  if (k === 'DRUG') return 'DRUG';
  if (k === 'OTC' || k === 'OTC_DRUG') return 'OTC';
  // The 4th category (Claim-valid-error-sample-data-fix-8-14.md §11.4 T-N.4).
  // The WIRE VALUE stays `NOT_APPLICABLE` so stored reports and the report
  // renderer keep working; only the label the user reads changes. This is the
  // product's PRIMARY question — "is this cosmetics or not?" — so it must
  // never silently fall through to the COSMETIC default below.
  if (k === 'NOT_APPLICABLE' || k === 'NON_COSMETIC') return 'NON-COSMETIC';
  return 'COSMETIC';
}

/* ------------------------------------------------------------------ *
 * Citation helpers — title/description serialization + FDA deep-link  *
 * ------------------------------------------------------------------ */
// Bare section code, with any " — description" tail stripped. The backend
// sometimes packs the whole "CODE — description" string into `section` and
// sets `source` to a prefix of it; this collapses the overlap so the code
// renders exactly once (fixes the duplicated-citation + [object Object] bugs).
export function citationTitle(c) {
  if (!c) return 'Reference';
  if (typeof c === 'string') return c.split(' — ')[0].trim() || 'Reference';
  const head = String(c.code || c.source || '').trim();
  let section = String(c.section || '').trim();
  const dash = section.indexOf(' — ');
  if (dash !== -1) section = section.slice(0, dash).trim();
  if (!head) return section || 'Reference';
  if (!section || section === head || head.startsWith(section)) return head;
  if (section.startsWith(head)) return section;
  return `${head} ${section}`;
}

// The prose description for a citation (never the code), wherever the backend
// stashed it: `text`, `description`, or the tail of a packed `section`/string.
export function citationDescription(c) {
  if (!c) return '';
  if (typeof c === 'string') {
    const i = c.indexOf(' — ');
    return i !== -1 ? c.slice(i + 3).trim() : '';
  }
  if (c.text) return String(c.text).trim();
  if (c.description) return String(c.description).trim();
  const section = String(c.section || '');
  const i = section.indexOf(' — ');
  return i !== -1 ? section.slice(i + 3).trim() : '';
}

const FDC_ACT_URL =
  'https://www.fda.gov/regulatory-information/laws-enforced-fda/federal-food-drug-and-cosmetic-act-fdc-act';

// Resolve a working FDA.gov / eCFR deep-link for a citation. Never returns an
// empty/apology state — falls back to a stable FDA.gov page. Used so "근거 보기"
// links straight to the official primary source instead of an in-app modal.
export function citationUrl(c) {
  if (c && typeof c === 'object' && c.url) return c.url;
  const title = citationTitle(c);
  const cfr = title.match(/21\s*CFR\s*(\d+)(?:\.(\d+))?/i);
  if (cfr) {
    return cfr[2]
      ? `https://www.ecfr.gov/current/title-21/section-${cfr[1]}.${cfr[2]}`
      : `https://www.ecfr.gov/current/title-21/part-${cfr[1]}`;
  }
  const upper = title.toUpperCase();
  if (upper.includes('MOCRA')) {
    return 'https://www.fda.gov/cosmetics/cosmetics-laws-regulations/modernization-cosmetics-regulation-act-2022-mocra';
  }
  if (upper.includes('FD&C') || upper.includes('FDC ACT') || /\b201\(/.test(title)) {
    return FDC_ACT_URL;
  }
  return 'https://www.fda.gov/cosmetics/cosmetics-laws-regulations';
}

export function CitationModal({ citation, onClose }) {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!citation) return null;
  const title = citationTitle(citation);
  const body = citationDescription(citation);
  const url = citationUrl(citation);
  return (
    <div className="rd-modal-overlay" role="presentation" onClick={onClose}>
      <div className="rd-modal" role="dialog" aria-modal="true" aria-label={title}
           onClick={(e) => e.stopPropagation()}>
        <div className="rd-modal-head">
          <span className="rd-modal-title">📄 {title}</span>
          <button type="button" className="rd-modal-close" aria-label={isKo ? '닫기' : 'Close'} onClick={onClose}>✕</button>
        </div>
        {body && <div className="rd-modal-body">{body}</div>}
        <div className="rd-modal-foot">
          <span className="rd-modal-source">{isKo ? '출처: FDA.gov' : 'Source: FDA.gov'}</span>
          <a className="rd-modal-extlink" href={url} target="_blank" rel="noopener noreferrer">
            {isKo ? 'FDA 공식 원문 보기 ↗' : 'View on FDA.gov ↗'}
          </a>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * KeyRulesTriggered — which regulatory rules fired (with View source)*
 * ------------------------------------------------------------------ */
export function KeyRulesTriggered({ ruleHits, collapsible = false }) {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const [open, setOpen] = useState(!collapsible);
  const hits = (ruleHits || []).filter((h) => h && (h.rule_name || h.citation));
  if (hits.length === 0) return null;
  const title = isKo ? '발동된 주요 규제 규칙' : 'Key Regulatory Rules Triggered';
  return (
    <section>
      {collapsible ? (
        <button type="button" className="rd-collapse-header" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <span className="rd-section-icon"><Icon.Gavel /></span>
          <h2 className="rd-section-title">{title}</h2>
          <span className="rd-collapse-count">{hits.length}</span>
          <span className="rd-collapse-chevron" data-open={open}><Icon.Chevron /></span>
        </button>
      ) : (
        <div className="rd-section-header">
          <span className="rd-section-icon"><Icon.Gavel /></span>
          <h2 className="rd-section-title">{title}</h2>
        </div>
      )}
      {open && (
      <ul className="rd-rules-list">
        {hits.map((h, i) => (
          <li key={i} className="rd-rule-row">
            <div className="rd-rule-main">
              <span className="rd-rule-name">{h.rule_name || (isKo ? '규칙' : 'Rule')}</span>
              {h.matched_terms?.length > 0 && (
                <span className="rd-rule-terms">{h.matched_terms.join(', ')}</span>
              )}
              {h.citation && <span className="rd-rule-cite">{h.citation}</span>}
            </div>
            {h.citation && (
              <a className="rd-viewsource-btn" href={citationUrl(h.citation)}
                 target="_blank" rel="noopener noreferrer">
                {isKo ? 'FDA 공식 원문 보기 ↗' : 'View on FDA.gov ↗'}
              </a>
            )}
          </li>
        ))}
      </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * RegulatoryReferences — citation list with in-app View source       *
 * ------------------------------------------------------------------ */
export function RegulatoryReferences({ citations, collapsible = false }) {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const [open, setOpen] = useState(!collapsible);
  // Dedupe identical provisions (same law must appear only once) keyed on the
  // collapsed title — the backend can emit the same citation more than once.
  const items = useMemo(() => {
    const seen = new Set();
    return (citations || []).filter(Boolean).filter((c) => {
      const key = citationTitle(c).toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [citations]);
  if (items.length === 0) return null;
  const title = isKo ? '규제 근거' : 'Regulatory References';
  return (
    <section>
      {collapsible ? (
        <button type="button" className="rd-collapse-header" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <span className="rd-section-icon"><Icon.Gavel /></span>
          <h2 className="rd-section-title">{title}</h2>
          <span className="rd-collapse-count">{items.length}</span>
          <span className="rd-collapse-chevron" data-open={open}><Icon.Chevron /></span>
        </button>
      ) : (
        <div className="rd-section-header">
          <span className="rd-section-icon"><Icon.Gavel /></span>
          <h2 className="rd-section-title">{title}</h2>
        </div>
      )}
      {open && (
      <ul className="rd-refs-list">
        {items.map((c, i) => (
          <li key={i} className="rd-ref-row">
            <span className="rd-ref-label">{citationTitle(c)}</span>
            <a className="rd-viewsource-btn" href={citationUrl(c)}
               target="_blank" rel="noopener noreferrer">
              {isKo ? 'FDA 공식 원문 보기 ↗' : 'View on FDA.gov ↗'}
            </a>
          </li>
        ))}
      </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * RiskAssessmentDetail — narrative detail under the risk cards       *
 * ------------------------------------------------------------------ */
export function RiskAssessmentDetail({ text }) {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  if (!text) return null;
  return (
    <section className="rd-risk-assessment">
      <div className="rd-section-header">
        <span className="rd-section-icon"><Icon.Gavel /></span>
        <h2 className="rd-section-title">{isKo ? '리스크 상세' : 'Risk Assessment'}</h2>
      </div>
      <p className="rd-detailed-text">{text}</p>
    </section>
  );
}

export function deriveReportId(report) {
  if (report?.short_id) return report.short_id;
  if (report?.id) {
    const id = String(report.id);
    const dateSeg = report.created_at
      ? new Date(report.created_at).toISOString().slice(0, 10).replace(/-/g, '')
      : '00000000';
    const tail = id.replace(/[^a-z0-9]/gi, '').slice(-4).toUpperCase();
    return `VP-${dateSeg}-${tail || '0000'}`;
  }
  return 'VP-XXXXXXXX-XXXX';
}

/* ------------------------------------------------------------------ *
 * ReportThemeToggle                                                   *
 * ------------------------------------------------------------------ */
export function ReportThemeToggle({ theme, onToggle }) {
  const { t } = useTranslation();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      className="rd-theme-toggle"
      aria-pressed={isDark}
      aria-label={isDark ? t('report.theme.toggleLight') : t('report.theme.toggleDark')}
      onClick={onToggle}
    >
      <span className="rd-theme-toggle-icon" data-visible={isDark ? 'false' : 'true'}>
        <Icon.Moon />
      </span>
      <span className="rd-theme-toggle-icon" data-visible={isDark ? 'true' : 'false'}>
        <Icon.Sun />
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * ReportHero                                                          *
 * ------------------------------------------------------------------ */
export function ReportHero({
  report,
  confidence,
  riskBucketKey,
  riskLevelEn,
  classification,
  hitCount,
  krJurisdiction,
}) {
  const { t } = useTranslation();
  const productName = report?.product_name || report?.title || '—';
  const reportId = deriveReportId(report);
  const hasConfidence = typeof confidence === 'number';
  // T5.1: never render the raw percentage as the headline figure — a single
  // context-free keyword hit on the veto path can display as high as 90%,
  // which the customer reads as a measurement. The real number is kept in
  // the DOM via `title` (debug-only surface) so support can still see it.
  const confidenceBandKey = confidenceBand(confidence);
  const confidenceTitle = hasConfidence ? `${(confidence * 100).toFixed(1)}%` : undefined;

  const isDisclaimer = riskBucketKey === 'disclaimer';
  const isReview = riskBucketKey === 'review';
  // Two distinct disclaimer outcomes share the 'disclaimer' bucket/styling but
  // need different copy — NOT_APPLICABLE (off-domain input, no regulated claim)
  // is not the same notice as ENGLISH_ONLY (non-English input not analyzed).
  const isNotApplicable = String(classification || '').toUpperCase().trim() === 'NOT_APPLICABLE';

  const isCritical = String(
    riskLevelEn ?? report?.analysis_result?.risk_level ?? report?.risk_level ?? '',
  ).toUpperCase() === 'CRITICAL';
  const cssBucket = _cssBucket(riskBucketKey);
  const bannerKey =
    isReview ? 'needsReview' :
    riskBucketKey === 'high'   ? (isCritical ? 'critical' : 'high') :
    riskBucketKey === 'medium' ? 'medium' : 'low';
  const countKey =
    riskBucketKey === 'high'                       ? 'countHigh' :
    (riskBucketKey === 'medium' || isReview)        ? 'countMedium' : 'countLow';
  const emoji =
    riskBucketKey === 'high'                 ? '🔴' :
    (riskBucketKey === 'medium' || isReview) ? '🟡' : '🟢';

  return (
    <section className={`rd-hero${riskBucketKey === 'low' ? ' rd-hero--safe' : ''}`}>
      <div className="rd-hero-inner">
        <div className="rd-hero-top">
          <div>
            <span className="rd-section-label rd-hero-id">
              {t('report.reportLabel')} · {reportId}
            </span>
            <h1 className="rd-hero-title">{productName}</h1>
          </div>
        </div>

        {isDisclaimer ? (
          /* ENGLISH_ONLY and NOT_APPLICABLE are disclaimer outcomes, not verdicts —
             deliberately NOT a 🔴/🟡/🟢 traffic light and never implies "usable"/
             "reviewed". Colored with neutral surface/border/text tokens instead
             of a risk color. Same layout, distinct copy per classification. */
          <div
            className="rd-risk-banner rd-risk-banner--disclaimer"
            style={{ background: 'var(--rd-surface-2)', border: '1px solid var(--rd-border)' }}
          >
            <div className="rd-risk-banner-title" style={{ color: 'var(--rd-on-surface)' }}>
              <span>{t(isNotApplicable ? 'report.notApplicable.title' : 'report.englishOnly.title')}</span>
            </div>
            <div className="rd-risk-banner-sub" style={{ color: 'var(--rd-muted)' }}>
              {t(isNotApplicable ? 'report.notApplicable.body' : 'report.englishOnly.body')}
            </div>
          </div>
        ) : (
          /* The verdict is the hero. Confidence is subordinate, tied to the
              verdict, and color-matched — never a standalone "safety" figure. */
          <div className={`rd-risk-banner rd-risk-banner--${cssBucket}`}>
            <div className="rd-risk-banner-title">
              <span>{emoji}</span>
              <span>{t(`report.banner.${bannerKey}`)}</span>
            </div>
            <div className="rd-risk-banner-sub">
              {hasConfidence ? (
                <>
                  <span className="rd-banner-confidence" title={confidenceTitle}>
                    {t('report.confidence')}: {t(`report.confidenceBand.${confidenceBandKey}`)}
                  </span>
                  {riskBucketKey !== 'low' && (
                    <span> · {t(`report.banner.${countKey}`, { count: hitCount })}</span>
                  )}
                </>
              ) : (
                t(`report.banner.${countKey}`, { count: hitCount })
              )}
            </div>
            {hasConfidence && (
              <div className="rd-risk-banner-note">{t('report.confidenceMicrocopy')}</div>
            )}
            {/* T3.1 — EVERY verdict states the ruleset that produced it.
                Before this, only the PASS case named a jurisdiction ("모든 표현이
                FDA 화장품 기준을 통과했습니다"), so a risk verdict read as
                absolute. The engine is FDA/21-CFR-only — grep for
                기능성|MFDS|화장품법 across app/agents, the detector and app/data
                returns ZERO hits; Korean is a display language here, not a
                regulatory jurisdiction. A Korean reviewer applying 기능성화장품
                rules to an unscoped 위험 reasonably reads it as wrong, which is
                what the 2026-09-16 report was really about.

                Deliberately renders on ALL buckets including 'low': stating the
                scope only when we pass is the asymmetry being fixed. */}
            <div className="rd-risk-banner-note rd-risk-banner-jurisdiction">
              {t('report.jurisdiction.scope')}
            </div>
            {/* T3.1c — the Korean reading, SUBORDINATE by design.
                Deliberately not a second traffic light: no colour, no risk
                level, no emoji status. There is ONE verdict on this screen and
                it is the US one, because that is the only rulebook the engine
                implements. This is the context that makes it interpretable —
                "Improves wrinkles" is a §201(g)(1)(C) structure/function claim
                in the US and the statutory name of a 기능성화장품 category
                (주름개선) in Korea, and the 2026-09-16 reporter was applying the
                second rulebook to a screen that only spoke the first.

                Renders nothing when the flag is off or the report predates it. */}
            {krJurisdiction && krJurisdiction.status && (
              <div className="rd-risk-banner-note rd-risk-banner-kr">
                <strong>{t('report.krJurisdiction.label')}</strong>
                {': '}
                {t(`report.krJurisdiction.${krJurisdiction.status}`, {
                  category: krJurisdiction.category_ko
                    || krJurisdiction.category_en || '',
                })}
                {krJurisdiction.registration_status_unknown && (
                  <span className="rd-kr-unknown">
                    {' '}
                    {t('report.krJurisdiction.registrationUnknown')}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * OriginalCopyAnalysis                                                *
 * ------------------------------------------------------------------ */
export function OriginalCopyAnalysis({ originalText, ruleHits, classification, riskBucketKey }) {
  const { t } = useTranslation();
  const isSafe = riskBucketKey === 'low';

  // Build a render plan: collect unique matched terms (case-insensitive) and
  // split text into pieces with terms highlighted.
  const allTerms = useMemo(() => {
    const seen = new Set();
    const out = [];
    (ruleHits || []).forEach((h) => {
      (h.matched_terms || []).forEach((term) => {
        const t2 = String(term || '').trim();
        if (!t2) return;
        const key = t2.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          out.push({ term: t2, label: classificationLabel(h.classification || classification) });
        }
      });
    });
    return out;
  }, [ruleHits, classification]);

  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const segments = useMemo(() => {
    if (!originalText) return [];
    if (allTerms.length === 0) return [{ type: 'text', value: originalText }];

    // Highlight matched terms at WORD BOUNDARIES ONLY.
    //
    // This used to be a bare alternation — `new RegExp('(' + terms + ')', 'gi')`
    // — which is why report VP-20260814-1D10 rendered the word "Kitchen" as
    // K·[itch]·en with a DRUG badge on the middle. It mirrored the same
    // substring bug the backend had (Claim-valid-fix-non-cosmetics-8-13.md);
    // both sides must use the same predicate or a correctly-matched backend
    // term can still highlight the wrong span here.
    //
    // Deliberately NOT using lookbehind — Safari only gained support for it in
    // 16.4, and a SyntaxError at RegExp construction would blank the whole
    // report. The leading boundary is captured as its own group and re-emitted
    // instead. The trailing inflection mirrors the backend's _INFLECTION so
    // "itchy" highlights as one unit rather than "itch" + a stray "y".
    const INFLECTION = '(?:ments|ment|ings|ing|ies|ous|ful|es|ed|ly|s|d|y)?';
    const sorted = [...allTerms].sort((a, b) => b.term.length - a.term.length);
    const pattern = new RegExp(
      `(^|[^A-Za-z0-9])(${sorted.map((t) => escapeRegex(t.term)).join('|')})${INFLECTION}(?![A-Za-z0-9])`,
      'gi',
    );

    const out = [];
    let cursor = 0;
    for (const m of originalText.matchAll(pattern)) {
      const lead = m[1] || '';
      const termStart = m.index + lead.length;
      const matched = m[0].slice(lead.length);
      if (termStart > cursor) {
        out.push({ type: 'text', value: originalText.slice(cursor, termStart) });
      }
      const hit = sorted.find((t) =>
        matched.toLowerCase().startsWith(t.term.toLowerCase()));
      out.push(hit
        ? { type: 'term', value: matched, label: hit.label }
        : { type: 'text', value: matched });
      cursor = termStart + matched.length;
    }
    if (cursor < originalText.length) {
      out.push({ type: 'text', value: originalText.slice(cursor) });
    }
    return out;
  }, [originalText, allTerms]);

  if (!originalText) return null;

  return (
    <section>
      <div className="rd-section-header">
        <span className="rd-section-icon"><Icon.Search /></span>
        <h2 className="rd-section-title">{t('report.original.title')}</h2>
      </div>
      <div className="rd-original-card">
        <p className="rd-original-text">
          “
          {segments.map((s, i) => {
            if (s.type === 'text') return <span key={i}>{s.value}</span>;
            if (isSafe) {
              return (
                <span key={i} className="rd-term-flag--safe">
                  <span className="rd-term-chip--safe-float">{t('report.original.safeChip')}</span>
                  <span className="rd-term-text--safe">{s.value}</span>
                </span>
              );
            }
            const chipMod = s.label === 'OTC' ? 'otc' : 'drug';
            const textMod = s.label === 'OTC' ? 'rd-term-text--otc' : '';
            return (
              <span key={i} className="rd-term-flag">
                <span className={`rd-term-chip rd-term-chip--${chipMod}`}>{s.label}</span>
                <span className={`rd-term-text ${textMod}`}>{s.value}</span>
              </span>
            );
          })}
          ”
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * DetailedDiagnosis                                                   *
 * ------------------------------------------------------------------ */
export function DetailedDiagnosis({ riskBucketKey, classification, citation }) {
  const { t } = useTranslation();
  // 'disclaimer' (ENGLISH_ONLY) is a neutral disclaimer outcome, not a verdict —
  // no fabricated legal-risk card for text that was never analyzed.
  if (riskBucketKey === 'low' || riskBucketKey === 'disclaimer') return null;

  const clsKey = String(classification || '').toUpperCase().trim();
  const isReview = clsKey === 'REVIEW_REQUIRED';

  // Probability label reflects the real computed severity bucket, not a fixed
  // string. The drug-specific "Class I Drug / FDA Warning Letter" body is shown
  // ONLY for DRUG/OTC classifications — a medium-risk cosmetic gets the neutral
  // structure/function caution instead of a fabricated drug-class assertion.
  const legalProbability = riskBucketKey === 'high'
    ? t('report.diagnosis.probabilityHigh')
    : t('report.diagnosis.probabilityModerate');
  const isDrugClass = ['DRUG', 'OTC', 'OTC_DRUG'].includes(clsKey);
  // REVIEW_REQUIRED (fail-safe sink: no evidence / low confidence) gets its own
  // 🟡 "Needs review" card — reusing the amber styling already used below for
  // Platform Ad Risk — instead of a fabricated legal-risk assertion.
  const legalTitle = isReview ? t('report.diagnosis.reviewTitle') : t('report.diagnosis.legalTitle');
  const legalBody = isReview
    ? t('report.diagnosis.reviewBody')
    : (isDrugClass ? t('report.diagnosis.legalBodyDrug') : t('report.diagnosis.legalBody'));
  const legalColorMod = isReview ? 'amber' : 'red';

  return (
    <section className="rd-diagnosis-grid">
      <div className="rd-diagnosis-card">
        <div className="rd-diagnosis-head">
          <span className={`rd-diagnosis-icon rd-diagnosis-icon--${legalColorMod}`}><Icon.Gavel /></span>
          <div>
            <h3 className="rd-diagnosis-title">{legalTitle}</h3>
            <p className={`rd-section-label rd-diagnosis-probability rd-diagnosis-probability--${legalColorMod}`}>
              {legalProbability}
            </p>
          </div>
        </div>
        <p className="rd-diagnosis-body">{legalBody}</p>
        {citation && <div className="rd-diagnosis-ref">REF: {citation}</div>}
      </div>

      <div className="rd-diagnosis-card">
        <div className="rd-diagnosis-head">
          <span className="rd-diagnosis-icon rd-diagnosis-icon--amber"><Icon.Megaphone /></span>
          <div>
            <h3 className="rd-diagnosis-title">{t('report.diagnosis.platformTitle')}</h3>
            {/* T1.4 (report-todo-fix-impl-830.md): this card is general platform
                guidance, not a per-report computed likelihood — it used to render
                the literal constant "중간 가능성"/"Moderate Probability" on every
                report, including clean ones, which reads as an assessment nobody
                computed. `platformNote` is a plain, ungraded label (no red/amber/
                green probability styling) so it can never be mistaken for a
                verdict about THIS claim. */}
            <p className="rd-section-label rd-diagnosis-probability">
              {t('report.diagnosis.platformNote')}
            </p>
          </div>
        </div>
        <p className="rd-diagnosis-body">{t('report.diagnosis.platformBody')}</p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * PrescriptionTable                                                   *
 * ------------------------------------------------------------------ */
// Phase 2 (Report-show-all-flagged-recommendation-8-23): the Prescription is now
// grouped into ONE CARD PER FLAGGED SENTENCE. `groups` = [{ sentence, flags }],
// where `sentence` is the original-input sentence (null for the legacy flat
// fallback) and `flags` = [{ flagged, safe, why }]. Each card shows the sentence,
// then lists every flagged phrase in it with its compliant alternative.
export function PrescriptionTable({ groups, onCopy }) {
  const { t } = useTranslation();
  if (!groups || groups.length === 0) return null;

  const renderFlags = (flags) => (
    <table>
      <thead>
        <tr>
          <th>{t('report.prescription.flagged')}</th>
          <th>{t('report.prescription.safeAlt')}</th>
          <th>{t('report.prescription.why')}</th>
          <th aria-hidden="true" />
        </tr>
      </thead>
      <tbody>
        {flags.map((r, i) => (
          <tr key={i}>
            <td><div className="rd-presc-flagged">{r.flagged}</div></td>
            <td><div className="rd-presc-safe">{r.safe || '—'}</div></td>
            <td><div className="rd-presc-why">{r.why || ''}</div></td>
            <td style={{ textAlign: 'right' }}>
              {r.safe && (
                <button
                  type="button"
                  className="rd-presc-copy-btn"
                  onClick={() => onCopy && onCopy(r.safe)}
                >
                  {t('report.prescription.copy')}
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <section>
      <div className="rd-section-header">
        <span className="rd-section-icon"><Icon.Healing /></span>
        <h2 className="rd-section-title">{t('report.prescription.title')}</h2>
      </div>
      <div className="rd-prescription">
        {groups.map((g, gi) => (
          <div key={gi} className="rd-presc-sentence-card">
            {g.sentence && (
              <p className="rd-presc-sentence">“{g.sentence}”</p>
            )}
            {renderFlags(g.flags)}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * SentenceVerdictTable — per-sentence CU results (T5.1).              *
 * Renders ONLY when the caller has confirmed `units` is present on   *
 * the AnalysisResult (CU metering on) — this component also          *
 * self-guards so direct/test usage with no data is a no-op.          *
 *                                                                      *
 * A unit with skipped_reason === 'unit_cap' WAS rule-checked (the cap  *
 * only skips the more expensive vector-similarity tier) — labeled     *
 * accordingly, never as "not analysed". skipped_reason === 'error'    *
 * reads as an honest failure, never disguised as a clean verdict.     *
 * Unit text is user-pasted — rendered as plain text, never            *
 * dangerouslySetInnerHTML.                                            *
 * ------------------------------------------------------------------ */
const UNIT_EXCERPT_LEN = 120;

export function SentenceVerdictTable({ units }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(() => new Set());
  if (!Array.isArray(units)) return null;

  const toggleRow = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <section>
      <div className="rd-section-header">
        <span className="rd-section-icon"><Icon.Sheet /></span>
        <h2 className="rd-section-title">{t('report.units.title')}</h2>
      </div>

      {units.length === 0 ? (
        <p className="rd-detailed-text">{t('report.units.empty')}</p>
      ) : (
        <div className="rd-prescription">
          <div style={{ maxHeight: 480, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>{t('report.units.columnSentence')}</th>
                  <th>{t('report.units.columnVerdict')}</th>
                  <th>{t('report.units.columnRisk')}</th>
                  <th>{t('report.units.columnConfidence')}</th>
                </tr>
              </thead>
              <tbody>
                {units.map((u, i) => {
                  const rowKey = u?.index ?? i;
                  const failed = u?.skipped_reason === 'error';
                  const capped = u?.skipped_reason === 'unit_cap';
                  const text = String(u?.text || '');
                  const isLong = text.length > UNIT_EXCERPT_LEN;
                  const isOpen = expanded.has(rowKey);
                  const shownText = !isLong || isOpen ? text : `${text.slice(0, UNIT_EXCERPT_LEN)}…`;

                  // Verdict label: reuses riskBucket()'s existing red/amber/green
                  // language + the same enums already used elsewhere on this page
                  // (never a fabricated new color/word).
                  let bucket = null;
                  let verdictText = null;
                  if (!failed) {
                    bucket = riskBucket(u?.risk_level, u?.classification, u?.confidence);
                    const cls = String(u?.classification || '').toUpperCase().trim();
                    if (cls === 'REVIEW_REQUIRED') verdictText = t('report.banner.needsReview');
                    else if (['DRUG', 'OTC_DRUG', 'OTC'].includes(cls)) verdictText = classificationLabel(cls);
                    else if (bucket === 'low') verdictText = t('report.original.safeChip');
                    else verdictText = classificationLabel(cls);
                  }
                  const cssBucket = failed ? '' : _cssBucket(bucket);
                  const emoji = failed ? '⚠️' : (cssBucket === 'high' ? '🔴' : cssBucket === 'medium' ? '🟡' : '🟢');
                  const colorVar =
                    cssBucket === 'high' ? 'var(--rd-risk-red-text)' :
                    cssBucket === 'medium' ? 'var(--rd-risk-amber-text)' :
                    cssBucket === 'low' ? 'var(--rd-risk-green-text)' : 'var(--rd-muted)';
                  const bgVar =
                    cssBucket === 'high' ? 'var(--rd-error-bg)' :
                    cssBucket === 'medium' ? 'var(--rd-warn-bg)' :
                    cssBucket === 'low' ? 'var(--rd-ok-bg)' : 'var(--rd-surface-2)';
                  const confidencePct = typeof u?.confidence === 'number'
                    ? Math.round(u.confidence * 100)
                    : null;
                  // T5.1: same band, not a headline percentage — this per-sentence
                  // verdict runs through the same ensemble/veto path as the overall
                  // report, so it is subject to the same veto-floor overstatement.
                  const confidenceBandKey = confidenceBand(u?.confidence);
                  // T2 (report-todo-fix-impl-830.md): this column shows evidence
                  // strength ("판정 근거 강도"), not the verdict — it must not reuse
                  // colorVar/bgVar, which are the ROW'S risk-bucket colors. Doing so
                  // was the same red-on-one-report/amber-on-another bug found in the
                  // hero and methodology accordion, just one column over. Fixed
                  // neutral color regardless of the row's verdict.
                  const confidenceColorVar = 'var(--rd-muted)';
                  const confidenceBgVar = 'var(--rd-surface-2)';

                  return (
                    <tr key={rowKey}>
                      <td>
                        <div style={{ color: 'var(--rd-on-surface)', fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word' }}>
                          {shownText}
                        </div>
                        {isLong && (
                          <button
                            type="button"
                            onClick={() => toggleRow(rowKey)}
                            style={{
                              background: 'none', border: 'none', padding: 0, marginTop: 4,
                              color: 'var(--rd-link, #6699FF)', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                              fontFamily: 'inherit',
                            }}
                          >
                            {isOpen ? t('report.units.showLess') : t('report.units.showFullText')}
                          </button>
                        )}
                        {capped && (
                          <div style={{ marginTop: 6, color: 'var(--rd-muted)', fontSize: 12, lineHeight: 1.4 }}>
                            {t('report.units.skippedReason.unit_cap')}
                          </div>
                        )}
                        {failed && (
                          <div style={{ marginTop: 6, color: 'var(--rd-risk-amber-text)', fontSize: 12, lineHeight: 1.4 }}>
                            {t('report.units.skippedReason.error')}
                          </div>
                        )}
                      </td>
                      <td>
                        {failed ? (
                          <span style={{ color: 'var(--rd-faint)' }}>—</span>
                        ) : (
                          <span style={{
                            display: 'inline-block', padding: '4px 10px', borderRadius: 999,
                            fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
                            color: colorVar, background: bgVar, whiteSpace: 'nowrap',
                          }}>
                            {verdictText}
                          </span>
                        )}
                      </td>
                      <td>
                        <span aria-hidden="true">{emoji}</span>
                        <span className="sr-only">{failed ? t('report.units.skippedReason.error') : verdictText}</span>
                      </td>
                      <td>
                        {confidenceBandKey ? (
                          <span
                            title={confidencePct != null ? `${confidencePct}%` : undefined}
                            style={{
                              display: 'inline-block', padding: '3px 8px', borderRadius: 999,
                              fontSize: 11, fontWeight: 700, color: confidenceColorVar, background: confidenceBgVar,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {t(`report.confidenceBand.${confidenceBandKey}`)}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--rd-faint)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * IgnoredUnitsPanel — non-cosmetics / skipped text, transparency not *
 * an error list (T5.2). Collapsed by default; count in the header.   *
 * Reuses the exact AccordionItem/.rd-method-item collapse primitive   *
 * already built for MethodologyAccordion — no second collapse widget. *
 * Renders ONLY when ignoredUnits is a non-empty array.                *
 * ------------------------------------------------------------------ */
// Human-ordered (not enum/alphabetical): most informative first.
const IGNORE_REASON_ORDER = ['off_topic', 'page_chrome', 'empty'];

export function IgnoredUnitsPanel({ ignoredUnits }) {
  const { t } = useTranslation();
  const items = Array.isArray(ignoredUnits) ? ignoredUnits.filter(Boolean) : [];
  if (items.length === 0) return null;

  const groups = IGNORE_REASON_ORDER
    .map((reason) => ({ reason, rows: items.filter((u) => u?.ignore_reason === reason) }))
    .filter((g) => g.rows.length > 0);
  const otherRows = items.filter((u) => !IGNORE_REASON_ORDER.includes(u?.ignore_reason));

  const listStyle = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 };
  const rowStyle = {
    background: 'var(--rd-surface-2)', borderRadius: 8, padding: '10px 12px',
    color: 'var(--rd-muted)', fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word',
  };

  return (
    <section>
      <AccordionItem
        title={t('report.ignoredUnits.title', { count: items.length })}
        defaultOpen={false}
        expandLabel={t('report.ignoredUnits.expand')}
        collapseLabel={t('report.ignoredUnits.collapse')}
      >
        <p style={{ margin: '0 0 16px', color: 'var(--rd-muted)', fontSize: 13.5, lineHeight: 1.6 }}>
          {t('report.ignoredUnits.explanation')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {groups.map((g) => (
            <div key={g.reason}>
              <div className="rd-section-label" style={{ marginBottom: 8 }}>
                {t(`report.ignoredUnits.reason.${g.reason}`)} · {g.rows.length}
              </div>
              <ul style={listStyle}>
                {g.rows.map((u, i) => (
                  <li key={u?.index ?? `${g.reason}-${i}`} style={rowStyle}>{u?.text}</li>
                ))}
              </ul>
            </div>
          ))}
          {otherRows.length > 0 && (
            <ul style={listStyle}>
              {otherRows.map((u, i) => (
                <li key={u?.index ?? `other-${i}`} style={rowStyle}>{u?.text}</li>
              ))}
            </ul>
          )}
        </div>
      </AccordionItem>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * RecommendedClaims                                                   *
 * ------------------------------------------------------------------ */
export function RecommendedClaims({ primary, secondary, onCopy, showTitle = true }) {
  const { t } = useTranslation();
  if (!primary && !secondary) return null;
  const isSingle = !secondary;

  return (
    <section>
      {showTitle && (
        <h2 className="rd-section-title" style={{ marginBottom: 24 }}>
          {t('report.recommend.title')}
        </h2>
      )}
      <div className={`rd-recommend-grid ${isSingle ? 'rd-recommend-grid--single' : ''}`}>
        {primary && (
          <div className="rd-recommend-primary">
            <div className="rd-recommend-head">
              <div>
                <span className="rd-recommend-badge">{t('report.recommend.primaryBadge')}</span>
                {typeof primary.matchPct === 'number' && (
                  <div className="rd-recommend-match">
                    {t('report.recommend.basedOn', { pct: primary.matchPct })}
                  </div>
                )}
              </div>
              {typeof primary.safety === 'number' && (
                <div className="rd-recommend-safety">
                  <div className="rd-recommend-safety-value">{primary.safety}%</div>
                  <span className="rd-section-label">{t('report.recommend.safetyIndex')}</span>
                </div>
              )}
            </div>
            <p className="rd-recommend-quote">“{primary.text}”</p>
            <button type="button" className="rd-recommend-cta" onClick={() => onCopy && onCopy(primary.text)}>
              {t('report.recommend.copy')}
            </button>
          </div>
        )}

        {secondary && (
          <div className="rd-recommend-secondary">
            <span className="rd-section-label" style={{ marginBottom: 16, display: 'block' }}>
              {t('report.recommend.secondaryBadge')}
            </span>
            <p className="rd-recommend-quote rd-recommend-quote--sm">“{secondary.text}”</p>
            {typeof secondary.matchPct === 'number' && (
              <div className="rd-recommend-match" style={{ marginBottom: 24 }}>
                {t('report.recommend.basedOn', { pct: secondary.matchPct })}
              </div>
            )}
            <div className="rd-recommend-bar-wrap">
              {typeof secondary.safety === 'number' && (
                <>
                  <div className="rd-recommend-bar-row">
                    <span className="rd-recommend-bar-label">{t('report.recommend.safetyRating')}</span>
                    <span className="rd-recommend-bar-value">{secondary.safety}%</span>
                  </div>
                  <div className="rd-recommend-bar-track">
                    <div className="rd-recommend-bar-fill" style={{ width: `${secondary.safety}%` }} />
                  </div>
                </>
              )}
              <button
                type="button"
                className="rd-recommend-secondary-cta"
                onClick={() => onCopy && onCopy(secondary.text)}
              >
                {t('report.recommend.select')}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * SafeRewrites — wires the previously-dormant RecommendedClaims into a *
 * real, data-driven list keyed off AnalysisResult.safe_rewrites (T5.6). *
 * Renders ONLY when the caller has confirmed safe_rewrites is a        *
 * non-empty array — this component also self-guards.                  *
 *                                                                       *
 * An abstained rewrite (no usable replacement was produced) is         *
 * rendered as an honest gap — never as RecommendedClaims' "here's a    *
 * great safe alternative" success card, which would misreport it as    *
 * "no issues found". See report.safeRewrites.abstained.                *
 * ------------------------------------------------------------------ */
export function SafeRewrites({ rewrites, onCopy }) {
  const { t } = useTranslation();
  const items = Array.isArray(rewrites) ? rewrites.filter(Boolean) : [];
  if (items.length === 0) return null;

  const SOURCE_KEYS = ['llm', 'deterministic_fallback', 'none'];

  return (
    <section>
      <div className="rd-section-header">
        <span className="rd-section-icon"><Icon.Healing /></span>
        <h2 className="rd-section-title">{t('report.safeRewrites.title')}</h2>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {items.map((item, i) => {
          const sourceKey = SOURCE_KEYS.includes(item?.source) ? item.source : 'none';
          const isAbstained = !!item?.abstained || !item?.rewritten_text;
          const flagged = item?.original_text && (
            <>
              <span className="rd-section-label">{t('report.prescription.flagged')}</span>
              <div className="rd-presc-flagged" style={{ marginTop: 4, marginBottom: 12, fontSize: 15 }}>
                {item.original_text}
              </div>
            </>
          );

          if (isAbstained) {
            return (
              <div
                key={i}
                style={{
                  background: 'var(--rd-surface-2)',
                  border: '1px solid var(--rd-border)',
                  borderRadius: 10,
                  padding: 20,
                }}
              >
                {flagged}
                <p style={{ margin: 0, color: 'var(--rd-muted)', fontSize: 13.5, lineHeight: 1.6 }}>
                  {t('report.safeRewrites.abstained')}
                </p>
                <span className="rd-section-label" style={{ marginTop: 12, display: 'block' }}>
                  {t(`report.safeRewrites.source.${sourceKey}`)}
                </span>
              </div>
            );
          }

          const safetyPct = typeof item?.confidence === 'number'
            ? Math.round(item.confidence * 100)
            : undefined;

          return (
            <div key={i}>
              {flagged}
              <span className="rd-section-label" style={{ marginBottom: 8, display: 'block' }}>
                {t('report.safeRewrites.suggestedReplacement')}
              </span>
              <RecommendedClaims
                primary={{ text: item.rewritten_text, safety: safetyPct }}
                secondary={null}
                onCopy={onCopy}
                showTitle={false}
              />
              <span className="rd-section-label" style={{ marginTop: 8, display: 'block', color: 'var(--rd-faint)' }}>
                {t(`report.safeRewrites.source.${sourceKey}`)}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * MarketingTip                                                        *
 * ------------------------------------------------------------------ */
export function MarketingTip({ bucket }) {
  const { t } = useTranslation();
  // ENGLISH_ONLY: nothing was analyzed, so there is no marketing copy to tip on.
  if (bucket === 'disclaimer') return null;
  const bodyKey = bucket === 'low' ? 'report.tip.bodyLow' : 'report.tip.body';
  return (
    <section className="rd-tip">
      <span className="rd-tip-icon"><Icon.Lightbulb /></span>
      <div>
        <span className="rd-section-label rd-tip-label">{t('report.tip.label')}</span>
        <p className="rd-tip-body">{t(bodyKey)}</p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * OptionalImprovements — appears on low-risk reports instead of      *
 * PrescriptionTable. Source: analysis_result.optional_improvements   *
 * with fallback to recommendations.slice(0, 2).                       *
 * ------------------------------------------------------------------ */
export function OptionalImprovements({ suggestions, onSelect }) {
  const { t } = useTranslation();
  const items = (suggestions || [])
    .map((s) => (typeof s === 'string' ? { text: s } : s))
    .filter((s) => s && s.text)
    .slice(0, 2);
  if (items.length === 0) return null;
  return (
    <section>
      <div className="rd-section-header">
        <span className="rd-section-icon" style={{ color: 'var(--rd-risk-green-text)' }}>
          <Icon.Bolt />
        </span>
        <h2 className="rd-section-title">{t('report.optional.title')}</h2>
      </div>
      <p className="rd-optional-lede">{t('report.optional.lede')}</p>
      <div className="rd-optional-grid">
        {items.map((s, i) => (
          <div key={i} className="rd-optional-card">
            <p className="rd-optional-quote">“{s.text}”</p>
            <button
              type="button"
              className="rd-optional-select"
              onClick={() => onSelect && onSelect(s.text)}
            >
              {t('report.optional.select')}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * MethodologyAccordion                                                *
 * ------------------------------------------------------------------ */
function AccordionItem({ title, children, defaultOpen = false, expandLabel, collapseLabel }) {
  const [open, setOpen] = useState(defaultOpen);
  // Optional aria-label override (e.g. report.ignoredUnits.expand/collapse) —
  // when not supplied, the accessible name falls back to the visible `title`
  // (unchanged for every existing caller, which doesn't pass these props).
  const ariaLabel = (expandLabel || collapseLabel) ? (open ? collapseLabel : expandLabel) : undefined;
  return (
    <div className="rd-method-item" data-open={open}>
      <button
        type="button"
        className="rd-method-trigger"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{title}</span>
        <span className="rd-method-chevron"><Icon.Chevron /></span>
      </button>
      {open && <div className="rd-method-content">{children}</div>}
    </div>
  );
}

// A similar-case entry is the real backend shape — a flat AnalysisResult.similar_cases
// row, e.g. { type: 'drug_precedent', claim_text, similarity, citation, source } — but
// this also accepts a bare number defensively (older/mocked callers) rather than
// rendering a broken "NaN%".
function _similarityPct(p) {
  const v = typeof p === 'number' ? p : Number(p?.similarity);
  return Number.isFinite(v) ? `${Math.round(v * 100)}%` : '—';
}

export function MethodologyAccordion({
  citations,
  similarCases,
  scoring,
  finalConfidence,
  riskBucketKey,
  defaultOpen = false,
  // T5.3: an empty similar-case list means one of two different things —
  // "the lookup ran and found nothing" vs. "the lookup could not run at all"
  // (unpopulated index / failed embedding — see AnalysisResult.degraded_reasons
  // 'vector_retrieval_failed' | 'embedding_failed'). Defaults false so every
  // existing caller keeps today's "ran, found nothing" wording unless it
  // explicitly knows otherwise.
  retrievalUnavailable = false,
}) {
  const { t } = useTranslation();
  const uniqueCites = useMemo(() => {
    const seen = new Set();
    // Key on the collapsed title (a real string). The old key stringified the
    // object — `[object Object]` for every backend citation — which collapsed
    // the whole list to one entry AND rendered as literal "[object Object]".
    return (citations || []).filter(Boolean).filter((c) => {
      const key = citationTitle(c).toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [citations]);

  const ruleEnginePct = scoring?.rule_engine != null ? Math.round(scoring.rule_engine * 100) : null;
  const vectorPct     = scoring?.vector     != null ? Math.round(scoring.vector * 100)     : null;
  const finalPct      = typeof finalConfidence === 'number' ? Math.round(finalConfidence * 100) : null;
  // T5.1: same band as the hero — never a headline percentage for the
  // ensemble confidence. Raw value stays available via `title`.
  const finalBand     = confidenceBand(finalConfidence);

  return (
    <section className="rd-method">
      <h2 className="rd-method-title">{t('report.method.title')}</h2>
      {/* T4.1 (report-todo-fix-impl-830.md): scope disclosure, extending this
          existing methodology section rather than adding a new one. States
          what was and was not checked — turns known gaps (no FTC
          substantiation axis, text-only / no image analysis) into an honest,
          stated limit instead of a silent miss. */}
      <p className="rd-method-scope">{t('report.method.scope')}</p>
      <div className="rd-method-list">
        <AccordionItem title={t('report.method.law')} defaultOpen={defaultOpen}>
          {uniqueCites.length === 0 ? (
            <p>{t('report.method.lawEmpty')}</p>
          ) : (
            <ul>
              {uniqueCites.map((c, i) => {
                const desc = citationDescription(c);
                return (
                  <li key={i}>
                    <span className="rd-method-bullet" />
                    <span>{citationTitle(c)}{desc ? ` — ${desc}` : ''}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </AccordionItem>

        <AccordionItem title={t('report.method.cases')} defaultOpen={defaultOpen}>
          {(!similarCases || (similarCases.drug?.length === 0 && similarCases.cosmetic?.length === 0)) ? (
            <p>{t(retrievalUnavailable ? 'report.method.casesUnavailable' : 'report.method.casesEmpty')}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {similarCases.drug?.length > 0 && (
                <div>
                  <div className="rd-section-label" style={{ color: 'var(--rd-risk-red-text)', marginBottom: 4 }}>
                    {t('report.method.drugMatch')}
                  </div>
                  <div style={{ color: '#fff', fontFamily: 'Consolas, monospace', fontSize: 13 }}>
                    {similarCases.drug.map((p) => _similarityPct(p)).join(' / ')}
                  </div>
                </div>
              )}
              {similarCases.cosmetic?.length > 0 && (
                <div>
                  <div className="rd-section-label" style={{ color: 'var(--rd-risk-green-text)', marginBottom: 4 }}>
                    {t('report.method.cosmeticMatch')}
                  </div>
                  <div style={{ color: '#fff', fontFamily: 'Consolas, monospace', fontSize: 13 }}>
                    {similarCases.cosmetic.map((p) => _similarityPct(p)).join(' / ')}
                  </div>
                </div>
              )}
            </div>
          )}
        </AccordionItem>

        <AccordionItem title={t('report.method.scoring')} defaultOpen={defaultOpen}>
          {ruleEnginePct == null && vectorPct == null && finalPct == null ? (
            <p>{t('report.method.scoreEmpty')}</p>
          ) : (
            <>
              {ruleEnginePct != null && (
                <div className="rd-score-row">
                  <div className="rd-score-row-top">
                    <span>{t('report.method.ruleEngine')}</span>
                    <span className="rd-score-value">{ruleEnginePct}%</span>
                  </div>
                  <div className="rd-score-bar">
                    <div className="rd-score-bar-fill" style={{ width: `${ruleEnginePct}%` }} />
                  </div>
                </div>
              )}
              {vectorPct != null && (
                <div className="rd-score-row">
                  <div className="rd-score-row-top">
                    <span>{t('report.method.vectorSearch')}</span>
                    <span className="rd-score-value">{vectorPct}%</span>
                  </div>
                  <div className="rd-score-bar">
                    <div className="rd-score-bar-fill" style={{ width: `${vectorPct}%` }} />
                  </div>
                </div>
              )}
              {finalBand && (
                <div className={`rd-score-final${_cssBucket(riskBucketKey) ? ` rd-score-final--${_cssBucket(riskBucketKey)}` : ''}`}>
                  <span className="rd-score-final-label">{t('report.method.final')}</span>
                  <span
                    className="rd-score-final-value"
                    title={finalPct != null ? `${finalPct}%` : undefined}
                  >
                    {t(`report.confidenceBand.${finalBand}`)}
                  </span>
                </div>
              )}
            </>
          )}
        </AccordionItem>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * ReportFooterActions                                                 *
 * ------------------------------------------------------------------ */
export function ReportFooterActions({
  showPdf,
  pdfLoading,
  canDownloadPdf,
  onDownloadPdf,
  onShare,
  onSheets,
  feedback,
  onFeedback,
}) {
  const { t } = useTranslation();
  return (
    <div className="rd-footer-actions">
      <div className="rd-footer-actions-row">
        {showPdf && (
          <button
            type="button"
            className="rd-footer-btn rd-footer-btn--primary"
            onClick={onDownloadPdf}
            disabled={!canDownloadPdf || pdfLoading}
          >
            <Icon.Pdf />
            {pdfLoading ? '…' : (canDownloadPdf ? t('report.actions.downloadPdf') : t('report.actions.pdfLocked'))}
          </button>
        )}
        {onSheets && (
          <button type="button" className="rd-footer-btn rd-footer-btn--ghost" onClick={onSheets}>
            <Icon.Sheet />{t('report.actions.sheets')}
          </button>
        )}
        <button type="button" className="rd-footer-btn rd-footer-btn--ghost" onClick={onShare}>
          <Icon.Share />{t('report.actions.share')}
        </button>
      </div>

      <div className="rd-helpful">
        <span className="rd-helpful-label">{t('report.actions.helpful')}</span>
        <button
          type="button"
          className="rd-helpful-btn"
          data-active={feedback === 'yes'}
          onClick={() => onFeedback && onFeedback('yes')}
        >
          <span>👍</span>{t('report.actions.yes')}
        </button>
        <button
          type="button"
          className="rd-helpful-btn"
          data-active={feedback === 'no'}
          onClick={() => onFeedback && onFeedback('no')}
        >
          <span>👎</span>{t('report.actions.no')}
        </button>
      </div>

      <div className="rd-disclaimer-card">
        <p>
          {t('report.disclaimer')}
          <a href="mailto:hello@whereat.kr">{t('report.contactExpert')} ↗</a>
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * YourAnswers — questionnaire summary (light mock §3)                 *
 * Renders only when answers exist on analysis_result.                 *
 * ------------------------------------------------------------------ */
export function YourAnswers({ answers }) {
  const { t } = useTranslation();
  if (!answers || typeof answers !== 'object') return null;

  // Normalize a small set of well-known questionnaire fields.
  // Unknown shapes are rendered as key/value rows.
  const rows = [];
  if ('has_clinical_evidence' in answers) {
    const v = answers.has_clinical_evidence;
    rows.push({
      q: t('report.your_answers.q_evidence'),
      a: v ? t('report.your_answers.ans_yes') : t('report.your_answers.ans_no'),
      note: v
        ? t('report.your_answers.evidence_note_positive')
        : t('report.your_answers.evidence_note'),
    });
  }
  Object.keys(answers).forEach((k) => {
    if (k === 'has_clinical_evidence') return;
    const v = answers[k];
    if (v === null || v === undefined || v === '') return;
    rows.push({
      q: k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      a: typeof v === 'boolean'
        ? (v ? t('report.your_answers.ans_yes') : t('report.your_answers.ans_no'))
        : String(v),
      note: null,
    });
  });

  if (rows.length === 0) return null;

  return (
    <section className="rd-your-answers">
      <span className="rd-your-answers-label">{t('report.your_answers.title')}</span>
      <div className="rd-your-answers-list">
        {rows.map((r, i) => (
          <div key={i}>
            <h4 className="rd-your-answers-q">{r.q}</h4>
            <p className="rd-your-answers-a">{r.a}</p>
            {r.note && (
              <div className="rd-your-answers-note">
                <span className="rd-arrow">→</span>{r.note}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
